import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 * @property {HTMLElement | null} activeSubmenu - Portaled dropdown submenu currently open.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement} [scrollPrev] - Previous scroll button.
 * @property {HTMLElement} [scrollNext] - Next scroll button.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #dropdownCloseTimer;

  /** @type {ResizeObserver | null} */
  #scrollResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    window.addEventListener('scroll', this.#onWindowScroll, true);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#bindScrollControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    window.removeEventListener('scroll', this.#onWindowScroll, true);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    if (this.#state.activeSubmenu) {
      this.#unportalDropdown(this.#state.activeSubmenu);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#unbindScrollControls();
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#dropdownCloseTimer);
    this.#dropdownCloseTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#updateScrollButtons();
    this.#repositionPortaledDropdown();
  }, 100);

  #onWindowScroll = () => {
    this.#repositionPortaledDropdown();
  };

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
    activeSubmenu: null,
  };

  /**
   * Horizontal scroller inside overflow-list shadow root.
   * @returns {HTMLElement | null}
   */
  #getMenuScroller() {
    const list = this.refs.overflowMenu?.shadowRoot?.querySelector('[part="list"]');
    return list instanceof HTMLElement ? list : null;
  }

  #bindScrollControls() {
    this.refs.scrollPrev?.addEventListener('click', this.#onScrollPrev);
    this.refs.scrollNext?.addEventListener('click', this.#onScrollNext);

    const scroller = this.#getMenuScroller();
    scroller?.addEventListener('scroll', this.#onMenuScroll, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      this.#scrollResizeObserver = new ResizeObserver(() => this.#updateScrollButtons());
      if (scroller) this.#scrollResizeObserver.observe(scroller);
      this.#scrollResizeObserver.observe(this);
    }

    requestAnimationFrame(() => this.#updateScrollButtons());
  }

  #unbindScrollControls() {
    this.refs.scrollPrev?.removeEventListener('click', this.#onScrollPrev);
    this.refs.scrollNext?.removeEventListener('click', this.#onScrollNext);
    this.#getMenuScroller()?.removeEventListener('scroll', this.#onMenuScroll);
    this.#scrollResizeObserver?.disconnect();
    this.#scrollResizeObserver = null;
  }

  #onScrollPrev = () => this.#scrollMenu(-1);
  #onScrollNext = () => this.#scrollMenu(1);

  #onMenuScroll = () => {
    this.#updateScrollButtons();
    this.#repositionPortaledDropdown();
  };

  /**
   * @param {number} direction
   */
  #scrollMenu(direction) {
    const scroller = this.#getMenuScroller();
    if (!scroller) return;
    const amount = Math.max(140, Math.round(scroller.clientWidth * 0.45));
    scroller.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }

  #updateScrollButtons = () => {
    const scroller = this.#getMenuScroller();
    const prev = this.refs.scrollPrev;
    const next = this.refs.scrollNext;
    if (!scroller || !(prev instanceof HTMLElement) || !(next instanceof HTMLElement)) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const canScroll = maxScroll > 4;

    if (!canScroll) {
      prev.hidden = true;
      next.hidden = true;
      prev.classList.remove('is-visible');
      next.classList.remove('is-visible');
      return;
    }

    const left = scroller.scrollLeft;
    const showPrev = left > 4;
    const showNext = left < maxScroll - 4;

    prev.hidden = !showPrev;
    next.hidden = !showNext;
    prev.classList.toggle('is-visible', showPrev);
    next.classList.toggle('is-visible', showNext);
  };

  /**
   * Move dropdown to document.body so overflow scroll never clips it.
   * @param {HTMLElement} item
   * @param {HTMLElement} submenu
   */
  #portalDropdown(item, submenu) {
    if (this.#state.activeSubmenu && this.#state.activeSubmenu !== submenu) {
      this.#unportalDropdown(this.#state.activeSubmenu);
    }

    if (submenu.dataset.portaled !== 'true') {
      submenu._portalHome = {
        parent: submenu.parentElement,
        next: submenu.nextSibling,
      };
      submenu.classList.add('menu-list__submenu--portaled');
      submenu.dataset.portaled = 'true';
      document.body.appendChild(submenu);
      submenu.addEventListener('pointerenter', this.#onPortaledPointerEnter);
      submenu.addEventListener('pointerleave', this.#onPortaledPointerLeave);
    }

    this.#state.activeSubmenu = submenu;
    submenu.dataset.active = '';
    this.#positionPortaledDropdown(item, submenu);
    requestAnimationFrame(() => this.#updateScrollButtons());
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLElement} submenu
   */
  #positionPortaledDropdown(item, submenu) {
    const rect = item.getBoundingClientRect();
    let left = rect.left;
    // Flush under the link so the pointer can enter without a dead gap
    const top = rect.bottom;

    submenu.style.setProperty('--dropdown-top', `${Math.round(top)}px`);
    submenu.style.setProperty('--dropdown-left', `${Math.round(left)}px`);

    requestAnimationFrame(() => {
      const width = submenu.getBoundingClientRect().width || 0;
      left = Math.min(left, window.innerWidth - width - 8);
      left = Math.max(8, left);
      submenu.style.setProperty('--dropdown-left', `${Math.round(left)}px`);
    });
  }

  #repositionPortaledDropdown = () => {
    const { activeItem, activeSubmenu } = this.#state;
    if (!activeItem || !activeSubmenu || activeSubmenu.dataset.portaled !== 'true') return;
    this.#positionPortaledDropdown(activeItem, activeSubmenu);
  };

  /**
   * @param {HTMLElement | null | undefined} submenu
   */
  #unportalDropdown(submenu) {
    if (!(submenu instanceof HTMLElement) || submenu.dataset.portaled !== 'true') return;

    submenu.removeEventListener('pointerenter', this.#onPortaledPointerEnter);
    submenu.removeEventListener('pointerleave', this.#onPortaledPointerLeave);

    const home = submenu._portalHome;
    if (home?.parent instanceof HTMLElement) {
      if (home.next instanceof Node && home.next.parentNode === home.parent) {
        home.parent.insertBefore(submenu, home.next);
      } else {
        home.parent.appendChild(submenu);
      }
    }

    submenu.classList.remove('menu-list__submenu--portaled');
    delete submenu.dataset.portaled;
    delete submenu.dataset.active;
    delete submenu._portalHome;
    submenu.style.removeProperty('--dropdown-top');
    submenu.style.removeProperty('--dropdown-left');

    if (this.#state.activeSubmenu === submenu) {
      this.#state.activeSubmenu = null;
    }

    requestAnimationFrame(() => this.#updateScrollButtons());
  }

  #onPortaledPointerEnter = () => {
    clearTimeout(this.#dropdownCloseTimer);
    this.#dropdownCloseTimer = undefined;
  };

  /**
   * @param {PointerEvent} event
   */
  #onPortaledPointerLeave = (event) => {
    const item = this.#state.activeItem;
    const listItem = item?.closest('.menu-list__list-item');
    if (event.relatedTarget instanceof Node && listItem?.contains(event.relatedTarget)) {
      return;
    }
    this.#deactivate();
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  get isDropdownLayout() {
    return this.dataset.submenuLayout === 'dropdown';
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    clearTimeout(this.#dropdownCloseTimer);
    this.#dropdownCloseTimer = undefined;

    const isMoreTrigger = event.target.slot === 'more';
    const item = findMenuItem(event.target);
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
      }
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      previouslyActiveOverflowItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    if (overflowItem && overflowItem !== item) {
      overflowItem.ariaExpanded = 'true';
    }

    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      if (!this.isDropdownLayout) {
        // Cleanup any existing mutation observer from previous menu activations
        this.#cleanupMutationObserver();

        // Monitor DOM mutations to catch deferred content injection (from section hydration)
        this.#submenuMutationObserver = new MutationObserver(() => {
          requestAnimationFrame(() => {
            // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
            requestAnimationFrame(() => {
              if (submenu.offsetHeight > 0) {
                this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
                this.#cleanupMutationObserver();
              }
            });
          });
        });
        this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

        // Auto-disconnect after 500ms to prevent memory leaks
        setTimeout(() => {
          this.#cleanupMutationObserver();
        }, 500);
      }
    }

    // Compact dropdown: keep header height unchanged (no mega underlay)
    if (this.isDropdownLayout) {
      this.headerComponent?.style.setProperty('--submenu-height', '0px');
      this.#setFullOpenHeaderHeight(0, this.#getHeaderVisibleHeight());
      this.style.setProperty('--submenu-opacity', '1');

      if (submenu && submenu !== this.overflowMenu) {
        this.#portalDropdown(item, submenu);
      } else if (this.#state.activeSubmenu) {
        this.#unportalDropdown(this.#state.activeSubmenu);
      }

      this.#startPointerTracking(item, previouslyActiveItem);
      return;
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = this.#state.activeSubmenu || findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    // Portaled dropdown: brief grace so pointer can reach the panel on body
    if (this.isDropdownLayout && this.#state.activeSubmenu) {
      clearTimeout(this.#dropdownCloseTimer);
      this.#dropdownCloseTimer = setTimeout(() => {
        this.#dropdownCloseTimer = undefined;
        const item = this.#state.activeItem;
        const submenu = this.#state.activeSubmenu;
        const listItem = item?.closest('.menu-list__list-item');
        if (submenu?.matches(':hover') || listItem?.matches(':hover') || item?.matches(':hover')) {
          return;
        }
        this.#deactivate();
      }, 100);
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    // Keep open while pointer is over the portaled dropdown panel
    if (this.#state.activeSubmenu?.matches(':hover')) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#dropdownCloseTimer);
    this.#dropdownCloseTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu =
      this.#state.activeSubmenu ||
      findSubmenu(item) ||
      (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      activeOverflowItem.ariaExpanded = 'false';
    }

    if (submenu && submenu !== this.overflowMenu) {
      this.#unportalDropdown(submenu);
    } else if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
